import React from 'react'
import classNames from 'classnames'
import PropTypes from 'prop-types'
import { useUncontrolled } from 'uncontrollable'
import useEventCallback from '@restart/hooks/useEventCallback'
import { CSSTransition } from 'react-transition-group'

import { CloseButton } from 'library'
import { alertIconMap } from 'library/utils/iconMaps'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import styles from './styles.module.scss'

const propTypes = {
  /**
   * The Alert visual variant
   *
   * @type {'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'dark' | 'light'}
   */
  variant: PropTypes.string,

  /**
   * Renders a properly aligned dismiss button, as well as
   * adding extra horizontal padding to the Alert.
   */
  isDismissible: PropTypes.bool,

  /**
   * Controls the visual state of the Alert.
   *
   * @controllable onClose
   */
  isVisible: PropTypes.bool,

  /**
   * Callback fired when alert is closed.
   *
   * @controllable isVisible
   */
  onClose: PropTypes.func,

  /**
   * Sets the text for alert close button.
   */
  closeLabel: PropTypes.string,

  /**
   * Sets the text for the alert heading
   */
  heading: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),

  /**
   * Changes style to display at full width
   */
  isTopBanner: PropTypes.bool,

  /**
   * Determines if an icon based on variant is shown
   */
  hasIcon: PropTypes.bool,

  /**
   * The transition type
   *
   * @type {'slideDown' | 'fadeIn' }
   */
  transitionType: PropTypes.string,
}

const defaultProps = {
  isVisible: false,
  closeLabel: 'Dismiss alert',
  onClose: null,
  isDismissible: true,
  variant: 'success',
  heading: null,
  isTopBanner: false,
  hasIcon: true,
  transitionType: null,
}

const controllables = {
  isVisible: 'onClose',
}

const Alert = React.forwardRef((uncontrolledProps, ref) => {
  const {
    isVisible,
    closeLabel,
    className,
    children,
    variant,
    onClose,
    isDismissible,
    isTopBanner,
    hasIcon,
    heading,
    transitionType,
    ...props
  } = useUncontrolled(uncontrolledProps, controllables)

  const handleClose = useEventCallback((event) => {
    if (onClose) onClose(false, event)
  })

  const alert = (
    <div
      ref={ref}
      role="alert"
      {...props}
      className={classNames(className, styles.alert, {
        [styles[variant]]: variant,
        [styles.isDismissible]: isDismissible,
        [styles.isTopBanner]: isTopBanner,
      })}
    >
      {heading && (
        <AlertTitle className={classNames({ [styles.isOnlyChild]: !children })}>
          {hasIcon && heading && (
            <>
              <FontAwesomeIcon icon={alertIconMap[variant]} />{' '}
            </>
          )}
          {heading}
        </AlertTitle>
      )}
      {isDismissible && (
        <CloseButton
          className={styles.closeButton}
          onClick={handleClose}
          closeLabel={closeLabel}
        />
      )}
      {hasIcon && !heading && (
        <>
          <FontAwesomeIcon icon={alertIconMap[variant]} />{' '}
        </>
      )}
      {children}
    </div>
  )

  if (!transitionType) return isVisible ? alert : null

  return (
    <CSSTransition
      classNames={{
        // appear: 'my-appear',
        // appearActive: 'my-active-appear',
        // appearDone: 'my-done-appear',
        enter: styles[`${transitionType}-enter`],
        enterActive: styles[`${transitionType}-enter-active`],
        enterDone: styles[`${transitionType}-enter-done`],
        exit: styles[`${transitionType}-exit`],
        exitActive: styles[`${transitionType}-exit-active`],
      }}
      timeout={300}
      in={isVisible}
      unmountOnExit
      ref={ref}
      onExited={handleClose}
      {...props}
    >
      {alert}
    </CSSTransition>
  )
})

Alert.displayName = 'Alert'
Alert.propTypes = propTypes
Alert.defaultProps = defaultProps

// It appears you shouldn't use actual headers in dialogs for accessibility?
const AlertTitle = ({ children, className, ...props }) => (
  <div {...props} className={classNames(className, styles.title)}>
    {children}
  </div>
)

export default Alert
export { AlertTitle }
